import React, { useId } from "react";
import { FEATHER_PATH, DROP_PATH, DROP_TRANSFORM, FEATHER_TRANSFORM } from "../admin/featherPaths";

// Colour presets. Override any of them via shaftColor / nibColor props.
// - "ink"  : engraved black feather + oxblood ink drop (default).
// - "gold" : aged matte-pewter feather (oxidized silver) for reversed (dark) sections.
const PRESETS = {
  ink: { shaft: "#1C1A17", nib: "#7A2E2E" },
  gold: { shaft: "#7E837E", nib: "#B0573F" },
  white: { shaft: "#FFFFFF", nib: "#FFFFFF" },
};

const ASPECT = 1337 / 848; // wide, diagonal feather

// Aged matte-pewter gradient: a soft satin highlight sinking into tarnished,
// deep-oxidized tones, as if the metal has settled into the paper over decades.
const PEWTER_STOPS = [
  ["0", "#B6B8AF"],
  ["0.2", "#9DA098"],
  ["0.45", "#82867F"],
  ["0.72", "#666a63"],
  ["0.9", "#565a53"],
  ["1", "#494c46"],
];

// Diagonal feather-quill emblem with an ink drop, vector-traced from the
// book-cover artwork. `height` sets the mark height; width scales to the aspect.
export const QuillLogo = ({
  className = "",
  height = 46,
  variant = "ink",
  shaftColor,
  nibColor,
}) => {
  const preset = PRESETS[variant] || PRESETS.ink;
  const shaft = shaftColor || preset.shaft;
  const nib = nibColor || preset.nib;
  const rawId = useId();
  const gid = "p" + rawId.replace(/[^a-zA-Z0-9]/g, "");
  const pewter = variant === "gold" && !shaftColor;

  return (
    <svg
      className={className}
      width={Math.round(height * ASPECT)}
      height={height}
      viewBox="0 0 1337 848"
      fill="none"
      role="img"
      aria-label="The Raven's Quill emblem: a feather quill with an ink drop"
      xmlns="http://www.w3.org/2000/svg"
    >
      {pewter && (
        <defs>
          <linearGradient id={gid} gradientUnits="objectBoundingBox" x1="0" y1="0" x2="0.92" y2="1">
            {PEWTER_STOPS.map(([o, c]) => (
              <stop key={o} offset={o} stopColor={c} />
            ))}
          </linearGradient>
          <clipPath id={gid + "c"}>
            <path d={FEATHER_PATH} transform={FEATHER_TRANSFORM} />
          </clipPath>
          <filter id={gid + "f"}>
            <feTurbulence type="fractalNoise" baseFrequency="0.11" numOctaves="2" seed="11" stitchTiles="stitch" />
            <feColorMatrix type="saturate" values="0" />
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.22" intercept="0" />
            </feComponentTransfer>
          </filter>
        </defs>
      )}
      <g transform={FEATHER_TRANSFORM}>
        <path d={FEATHER_PATH} fill={pewter ? `url(#${gid})` : shaft} stroke="none" />
        <path d={DROP_PATH} transform={DROP_TRANSFORM} fill={nib} stroke="none" />
      </g>
      {pewter && (
        <rect x="0" y="0" width="1337" height="848" clipPath={`url(#${gid}c)`} filter={`url(#${gid}f)`} />
      )}
    </svg>
  );
};
